"use client";

import type { ContentfulAssetFragment, Maybe } from "@server/gql/graphql";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { ContentfulResponsiveVideo } from "../ContentfulResponsiveVideo";

type ArticleMediaPlaybackProps = {
  children?: ReactNode;
  className?: string;
  desktopVideo?: Maybe<ContentfulAssetFragment>;
  mobileVideo?: Maybe<ContentfulAssetFragment>;
  sourceMedia?: string;
  when?: string;
  poster?: string;
};

export function ArticleMediaPlayback({
  children,
  className,
  desktopVideo,
  mobileVideo,
  sourceMedia,
  when,
  poster,
}: ArticleMediaPlaybackProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setActive(entry.isIntersecting);
        if (entry.isIntersecting) setSeen(true);
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = ref.current?.querySelector("video");
    if (!video) return;
    if (active) {
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [active, seen]);

  return (
    <div ref={ref} className={cn("relative", !children && className)}>
      {children}
      {seen ? (
        <ContentfulResponsiveVideo
          className={cn(
            children && "absolute inset-0 h-full w-full",
            className,
          )}
          desktopVideo={desktopVideo}
          mobileVideo={mobileVideo}
          sourceMedia={sourceMedia}
          when={when}
          poster={poster}
          autoPlay={active}
        />
      ) : null}
    </div>
  );
}
